import * as React from 'react';

type Options = {
  wrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  // либо selector внутри wrapper для поиска элементов, либо селектор одного конкретного таргета
  childSelector?: string;
  // если нужно указать индекс среди найденных childSelector элементов
  index?: number;
  // если нужно передать функцию, возвращающую элемент напрямую
  getElement?: () => HTMLElement | null;
  // центрировать карточку (true) или просто прокрутить минимально, чтобы она оказалась видимой (false)
  center?: boolean;
  // поведение прокрутки
  behavior?: ScrollBehavior;
  // горизонтально по умолчанию
  horizontalOnly?: boolean;
};

export const useEnsureVisible = ({
  wrapperRef,
  childSelector,
  index,
  getElement,
  center = true,
  behavior = 'smooth',
  horizontalOnly = true
}: Options) => {
  const ensure = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    let el: HTMLElement | null = null;
    if (getElement) {
      el = getElement();
    } else if (childSelector) {
      const nodes = wrapper.querySelectorAll<HTMLElement>(childSelector);
      if (typeof index === 'number') {
        el = nodes[index] ?? null;
      } else {
        el = nodes[0] ?? null;
      }
    }

    if (!el) {
      return;
    }

    // если horizontalOnly == true, не трогаем вертикаль
    if (horizontalOnly) {
      const visibleStart = wrapper.scrollLeft;
      const visibleEnd = visibleStart + wrapper.clientWidth;

      const cardStart = el.offsetLeft;
      const cardEnd = cardStart + el.offsetWidth;

      // если элемент уже видим полностью — не трогаем
      if (cardStart >= visibleStart && cardEnd <= visibleEnd) {
        return;
      }

      let desiredLeft: number;
      if (center) {
        desiredLeft =
          el.offsetLeft + el.offsetWidth / 2 - wrapper.clientWidth / 2;
      } else {
        // минимальное смещение, чтобы элемент стал видимым
        if (cardStart < visibleStart) {
          desiredLeft = cardStart;
        } else {
          desiredLeft = cardEnd - wrapper.clientWidth;
        }
      }

      const maxLeft = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      const finalLeft = Math.max(0, Math.min(desiredLeft, maxLeft));

      wrapper.scrollTo({ left: finalLeft, behavior });
    } else {
      // полный scrollIntoView с опцией только для блока, но избегаем вертикальные сдвиги:
      el.scrollIntoView({
        behavior,
        block: center ? 'center' : 'nearest',
        inline: center ? 'center' : 'nearest'
      });
    }
  }, [
    wrapperRef,
    childSelector,
    index,
    getElement,
    center,
    behavior,
    horizontalOnly
  ]);

  return ensure;
};

export default useEnsureVisible;
